import authApiService from "./userApiService";

const handleError = (error) => {
  console.error("Tax Management API Error:", error);
  return {
    success: false,
    error: error.response?.data?.error || error.message || "An error occurred",
    statusCode: error.response?.status
  };
};

const handleSuccess = (response) => ({
  success: true,
  data: response.data?.data ?? response.data,
  message: response.data?.message,
  statusCode: response.status
});

export const taxManagementService = {
  /**
   * Fetch all tax settings for positions (Admin only, with transparent fallback to rates for cashier/floor).
   */
  async getTaxSettings() {
    try {
      const response = await authApiService.get("/tax-management");
      return handleSuccess(response);
    } catch (error) {
      if (error.response?.status === 403) {
        try {
          const ratesRes = await authApiService.get("/tax-management/rates");
          const ratesMap = ratesRes.data?.data || {};
          return {
            success: true,
            data: Object.values(ratesMap)
          };
        } catch {
          // ignore
        }
      }
      return handleError(error);
    }
  },

  /**
   * Fetch active tax rates map for tip entry modals (Operational).
   */
  async getPublicTaxRates() {
    try {
      const response = await authApiService.get("/tax-management/rates");
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Fetch tax change audit logs (Admin only).
   */
  async getTaxChangeLogs(limit = 100) {
    try {
      const response = await authApiService.get("/tax-management/logs", { params: { limit } });
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Create custom tax change log entry (Admin only).
   */
  async createTaxChangeLog(data) {
    try {
      const response = await authApiService.post("/tax-management/logs", data);
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Fetch tax setting for a specific position (Admin only).
   */
  async getTaxSettingByPosition(position) {
    try {
      const response = await authApiService.get(`/tax-management/${encodeURIComponent(position)}`);
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Update tax setting for a specific position (Admin only).
   */
  async updateTaxSetting(position, data) {
    try {
      const response = await authApiService.put(`/tax-management/${encodeURIComponent(position)}`, data);
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Bulk update multiple position tax settings (Admin only).
   */
  async bulkUpdateTaxSettings(settings, effectiveDate = null) {
    try {
      const response = await authApiService.put("/tax-management", { settings, effective_date: effectiveDate });
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Calculate dynamic tax on the server.
   */
  async calculateDynamicTax(position, amount, date) {
    try {
      const response = await authApiService.post("/tax-management/calculate", {
        position,
        amount,
        date
      });
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Reset all settings to defaults (Admin only).
   */
  async resetDefaults(effectiveDate = null) {
    try {
      const response = await authApiService.post("/tax-management/reset-defaults", { effective_date: effectiveDate });
      return handleSuccess(response);
    } catch (error) {
      return handleError(error);
    }
  }
};

export default taxManagementService;

